const WORKOS_AUTHORIZE_URL = "https://api.workos.com/user_management/authorize";

export class RefreshTokenRequestError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "RefreshTokenRequestError";
  }
}

interface ApiErrorBody {
  message?: string;
  error?: {
    message?: string;
  };
}

async function readApiErrorBody(res: Response): Promise<ApiErrorBody> {
  try {
    return (await res.json()) as ApiErrorBody;
  } catch {
    return {};
  }
}

function getClientId(): string {
  const id = import.meta.env.VITE_WORKOS_CLIENT_ID;
  if (!id) throw new Error("VITE_WORKOS_CLIENT_ID not set");
  return id;
}

/** Build the WorkOS AuthKit authorize redirect URL. */
export function buildAuthUrl(state?: string): string {
  const params = new URLSearchParams({
    client_id: getClientId(),
    redirect_uri: `${window.location.origin}/callback`,
    response_type: "code",
    provider: "authkit",
  });
  if (state) params.set("state", state);
  return `${WORKOS_AUTHORIZE_URL}?${params.toString()}`;
}

interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  userId: string;
}

/** Exchange the authorization code for tokens via our API. */
export async function exchangeCode(code: string): Promise<AuthResponse> {
  const res = await fetch("/v1/auth/callback", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  });
  if (!res.ok) {
    const body = await readApiErrorBody(res);
    throw new Error(
      body.error?.message ?? body.message ?? "Code exchange failed",
    );
  }
  return res.json() as Promise<AuthResponse>;
}

interface RefreshResponse {
  accessToken: string;
  refreshToken: string;
}

/** Refresh the access token using the refresh token. */
export async function refreshAccessToken(refreshToken: string): Promise<RefreshResponse> {
  const res = await fetch("/v1/auth/refresh", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });
  if (!res.ok) {
    const body = await readApiErrorBody(res);
    throw new RefreshTokenRequestError(
      body.error?.message ?? body.message ?? "Token refresh failed",
    );
  }
  return res.json() as Promise<RefreshResponse>;
}

/** Revoke the current session. */
export async function logout(accessToken: string): Promise<void> {
  await fetch("/v1/auth/logout", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  });
}

/**
 * Decode the `exp` claim from a JWT without a library.
 * Returns epoch milliseconds, or null if decoding fails.
 */
export function getTokenExpiry(jwt: string): number | null {
  try {
    const payload = jwt.split(".")[1];
    if (!payload) return null;
    const decoded = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return typeof decoded.exp === "number" ? decoded.exp * 1000 : null;
  } catch {
    return null;
  }
}
